import {useQuery} from '@tanstack/react-query';
import {get_refresh_time} from 'common';
import {query_closed_orders, query_order_states, query_orders} from 'connector/order';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {IOrder, IOrderUpdate} from 'types';
import {queryClient} from './marketHooks';
import {useAuth, useBalance} from './userHooks';

export function useActiveOrders() {
	const refresh_time = get_refresh_time();

	const {is_logged_in} = useAuth();
	const {refresh_forced: refresh_balance, isFetching: is_balance_fetching} = useBalance();

	const {data: order_list = [], isFetching: is_fetching_list} = useQuery<IOrder[], Error, IOrder[], ['orders']>({
		queryKey: ['orders'],
		queryFn: async (params: any) => await query_orders(params.queryKey[1]),
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchIntervalInBackground: false,

		staleTime: 3600 * 1000, // 1 hour

		enabled: is_logged_in,
	});

	const update_orders = useCallback((updater: (oldOrders: IOrder[]) => IOrder[]) => queryClient.setQueryData<IOrder[]>(['orders'], old => updater(old ?? [])), [queryClient]);

	const add_order = useCallback((order: IOrder) => update_orders(old => [...old, order]), [update_orders]);
	const remove_order = useCallback((order_id: string) => update_orders(old => old.filter(order => order.order_id !== order_id)), [update_orders]);
	const adjust_order = useCallback((new_order: IOrder) => update_orders(old => old.map(order => (order.order_id === new_order.order_id ? new_order : order))), [update_orders]);

	const refresh_orders = useCallback(async () => {
		await queryClient.invalidateQueries({queryKey: ['orders'], refetchType: 'all'});
		await queryClient.invalidateQueries({queryKey: ['orderStates'], refetchType: 'all'});
	}, [queryClient, is_logged_in]);

	const refresh_state = useCallback(
		async (order_id: string) => {
			await queryClient.invalidateQueries({
				queryKey: ['orderStates'],
				predicate: query => Array.isArray(query.queryKey[1]) && query.queryKey[1].includes(order_id),
			});

			const current = queryClient.getQueryData<IOrder[]>(['orders']) || [];
			const target = current.find(order => order.order_id === order_id);

			if (target && target.state === 'closed') {
				const [updatedOrder] = await query_orders([order_id]);
				if (updatedOrder) adjust_order(updatedOrder);
			}
		},
		[queryClient, adjust_order],
	);

	const orders_to_refresh = useMemo(
		() =>
			order_list
				.filter(order => order.state != 'closed' && order.state != 'canceled')
				.map(order => order.order_id)
				.filter(Boolean),
		[order_list],
	);

	const {data: states_set, isLoading: is_fetching_state} = useQuery<Record<string, IOrderUpdate>, Error, Record<string, IOrderUpdate>, ['orderStates', string[]]>({
		queryKey: ['orderStates', orders_to_refresh],
		queryFn: query_order_states,
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchIntervalInBackground: false,

		staleTime: 5000,

		refetchInterval: refresh_time,
		enabled: orders_to_refresh.length > 0 && is_logged_in,
	});

	const refresh_order_state = useCallback(
		async (states: any) => {
			if (!states || !Object.keys(states).length) return;

			for (const old_order of order_list) {
				const new_order = states[old_order.order_id];
				if (new_order && ((new_order.state === 'open' && old_order.state === 'opening') || (new_order.state === 'closed' && old_order.state === 'closing'))) {
					await refresh_balance();
					break;
				}
			}

			update_orders(old =>
				old.map(order => {
					const newOrderData = states[order.order_id];
					if (!newOrderData) return order;
					else {
						const is_changed: boolean = Object.entries(newOrderData).some(([key, value]) => value !== undefined && (order as any)[key] !== value);
						return is_changed ? {...order, ...newOrderData} : order;
					}
				}),
			);
		},
		[order_list, update_orders, refresh_balance],
	);

	useEffect(() => {
		refresh_order_state(states_set);
	}, [states_set, refresh_orders]);

	const active_orders = useMemo(() => order_list.filter(order => !order.archived), [order_list]);

	const [tvl, set_tvl] = useState<number>(0);
	const [profit, set_profit] = useState<number>(0);

	useEffect(() => {
		if (is_balance_fetching) return;
		const new_tvl = active_orders
			.filter(order => order.state !== 'closed' && order.state !== 'canceled' && order.state !== 'opening')
			.reduce((acc, order) => acc + order.tvl, 0);
		set_tvl(new_tvl);
	}, [active_orders, is_balance_fetching]);

	useEffect(() => {
		if (is_balance_fetching) return;
		const new_profit = active_orders
			.filter(order => order.state !== 'closed' && order.state !== 'canceled' && order.state !== 'opening')
			.reduce((acc, order) => acc + order.profit, 0);
		set_profit(new_profit);
	}, [active_orders, is_balance_fetching]);

	return {
		order_list,
		active_orders,
		tvl,
		profit,
		add_order,
		remove_order,
		adjust_order,
		refresh_orders,
		refresh_state,
		is_fetching_list,
		is_fetching_state,
	};
}

export function useClosedOrders() {
	const {is_logged_in} = useAuth();

	const {data, isFetching} = useQuery({
		queryKey: ['orders_closed'],
		queryFn: query_closed_orders,
		retry: (failureCount, error) => (error as any).status !== 404 && failureCount < 3,
		retryDelay: 3000,

		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchIntervalInBackground: false,

		staleTime: 5000,

		enabled: is_logged_in,
	});

	const refresh_forced = useCallback(async () => {
		is_logged_in && (await queryClient.invalidateQueries({queryKey: ['orders_closed']}));
	}, [queryClient, is_logged_in]);

	const total_profit = useMemo(() => {
		if (!data) return 0;
		else return data.reduce((acc, order) => acc + order.profit, 0);
	}, [data]);

	return {orders: data || [], total_profit: total_profit ?? 0, refresh_forced, isFetching};
}
